import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { FaTimes, FaPlus, FaMinus, FaTrash, FaShoppingBasket, FaArrowRight } from "react-icons/fa";
import { BASE_URL } from "../backend/backend.js";

const CartSidebar = ({ 
  cartItems = [], 
  onClose, 
  addToCart = () => {},      // Default to empty function to prevent crash
  removeFromCart = () => {}, 
  deleteFromCart = () => {} 
}) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [deleteMessage, setDeleteMessage] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const navigate = useNavigate();

  // 1. Check Login Status
  useEffect(() => {
    const user = localStorage.getItem("user");
    if (user) setIsLoggedIn(true);
  }, []);

  const validCartItems = Array.isArray(cartItems) ? cartItems : [];

  // 2. Calculations
  const totalBill = validCartItems.reduce((t, i) => t + (i.medicine?.price || 0) * (i.quantity || 1), 0);
  const afterDiscountBill = validCartItems.reduce((t, i) => 
    t + parseFloat(((i.medicine?.descountPrice || i.medicine?.price || 0) * (i.quantity || 1)).toFixed(2)), 0
  );

  // 3. Centralized Action Wrapper (For Loading Bar)
  const handleCartAction = async (actionFn, ...args) => {
    if (isUpdating) return;
    setIsUpdating(true);
    try {
      await actionFn(...args);
    } catch (err) {
      console.error("Action failed", err);
    } finally {
      // Delay slightly for smoother visual feedback
      setTimeout(() => setIsUpdating(false), 300);
    }
  };

  const handleDeleteItem = async (cartItemId) => {
    setIsUpdating(true);
    try {
      const res = await fetch(`${BASE_URL}/user/deleteCartProduct/${cartItemId}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (res.status === 401) {
        localStorage.removeItem("user");
        setIsLoggedIn(false);
        setDeleteMessage({ text: "Session expired. Redirecting...", type: "error" });
        setTimeout(() => handleNavigation("/login"), 2000);
        return;
      }

      const data = await res.json();
      if (res.ok) {
        setDeleteMessage({ text: "Removed", type: "success", itemId: cartItemId });
        deleteFromCart(cartItemId); 
      }
    } catch (error) {
      setDeleteMessage({ text: "Error deleting item", type: "error", itemId: cartItemId });
    } finally {
      setIsUpdating(false);
      setTimeout(() => setDeleteMessage(null), 2000);
    }
  };

  const handleNavigation = (path) => {
    onClose();
    navigate(path);
  };

  return (
    <motion.div
      initial={{ x: "100%" }}
      animate={{ x: 0 }}
      exit={{ x: "100%" }}
      transition={{ type: "spring", stiffness: 300, damping: 30 }}
      style={cartContainerStyle}
    >
      {/* --- TOP LOADING BAR --- */}
      {isUpdating && (
        <motion.div
          initial={{ scaleX: 0, originX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 0.5, repeat: Infinity }}
          style={loadingBarStyle}
        />
      )}

      {/* Header */}
      <div style={headerStyle}>
        <div>
          <h2 style={titleStyle}>🛒 My Cart</h2>
          <span style={itemCountStyle}>{validCartItems.length} items selected</span>
        </div>
        <motion.button onClick={onClose} style={closeButtonStyle} whileHover={{ rotate: 90 }} whileTap={{ scale: 0.8 }}>
          <FaTimes size={24} />
        </motion.button>
      </div>

      {/* Scrollable Area */}
      <div style={scrollAreaStyle}>
        <AnimatePresence mode="popLayout">
          {validCartItems.length === 0 ? (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} style={emptyCartContainerStyle}>
              <div style={emptyCartIconWrapper}><FaShoppingBasket size={50} color="#cbd5e0" /></div>
              <h3 style={emptyHeading}>{isLoggedIn ? "Your cart is empty!" : "Your cart is waiting!"}</h3>
              <p style={emptyText}>
                {isLoggedIn 
                  ? "Browse our medicines and add them to your cart." 
                  : "Log in to unlock exclusive discounts and seamless tracking."}
              </p>
              <button 
                style={primaryBtn} 
                onClick={() => handleNavigation(isLoggedIn ? "/mp" : "/login")}
              >
                {isLoggedIn ? "Browse Medicines" : "Log In"}
              </button>
            </motion.div>
          ) : (
            validCartItems.map((item) => (
              <motion.div
                key={item.id}
                layout
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 50 }}
                style={itemCardStyle}
              >
                <div style={itemHeader}>
                  <div style={{flex: 1}}>
                    <h4 style={itemName}>{item.medicine?.medicineName || "Medicine"}</h4>
                    <p style={itemMeta}>{item.medicine?.medineType} • {item.medicine?.manufacturerName}</p>
                  </div>
                  <button 
                    onClick={() => handleDeleteItem(item.id)} 
                    style={deleteBtn}
                    disabled={isUpdating}
                  >
                    <FaTrash size={14} />
                  </button>
                </div>

                <div style={itemFooter}>
                  <div style={qtyControl}>
                    <button style={qtyBtn} onClick={() => handleCartAction(removeFromCart, item.id)} disabled={isUpdating}>
                      <FaMinus size={10} />
                    </button>
                    <span style={qtyValue}>{item.quantity}</span>
                    <button style={qtyBtn} onClick={() => handleCartAction(addToCart, item.medicine)} disabled={isUpdating}>
                      <FaPlus size={10} />
                    </button>
                  </div>
                  <div style={priceArea}>
                    <span style={oldPrice}>₹{(item.medicine?.price * item.quantity).toFixed(2)}</span>
                    <span style={newPrice}>₹{(item.medicine?.descountPrice * item.quantity).toFixed(2)}</span>
                  </div>
                </div>
                {deleteMessage?.itemId === item.id && (
                  <p style={{ color: deleteMessage.type === 'error' ? '#e74c3c' : '#27ae60', fontSize: '0.75rem', margin: '5px 0 0' }}>
                    {deleteMessage.text}
                  </p>
                )}
              </motion.div>
            ))
          )}
        </AnimatePresence>
      </div>

      {/* Footer / Summary */}
      {validCartItems.length > 0 && (
        <div style={footerStyle}>
          <div style={summaryRow}><span>Subtotal</span><span>₹{totalBill.toFixed(2)}</span></div>
          <div style={summaryRow}><span style={{color: "#27ae60"}}>Savings</span><span style={{color: "#27ae60"}}>-₹{(totalBill - afterDiscountBill).toFixed(2)}</span></div>
          <div style={totalRow}><span>Total</span><span>₹{afterDiscountBill.toFixed(2)}</span></div>
          <button 
            style={checkoutBtn} 
            onClick={() => handleNavigation("/os")}
            disabled={isUpdating}
          >
            Proceed to Checkout <FaArrowRight style={{marginLeft: '10px'}} />
          </button>
        </div>
      )}
    </motion.div>
  );
};

// --- Styles (Responsive & Premium) ---
const cartContainerStyle = { position: "fixed", top: 0, right: 0, width: "min(420px, 100vw)", height: "100vh", background: "#fff", boxShadow: "-10px 0 40px rgba(0,0,0,0.1)", zIndex: 2000, display: "flex", flexDirection: "column", fontFamily: "'Poppins', sans-serif" };
const loadingBarStyle = { position: "absolute", top: 0, left: 0, height: "4px", width: "100%", background: "linear-gradient(90deg, #3498db, #2ecc71)", zIndex: 2100 };
const headerStyle = { padding: "25px", borderBottom: "1px solid #f1f1f1", display: "flex", justifyContent: "space-between", alignItems: "center" };
const titleStyle = { margin: 0, fontSize: "1.5rem", fontWeight: 700, color: "#2c3e50" };
const itemCountStyle = { fontSize: "0.85rem", color: "#95a5a6" };
const closeButtonStyle = { background: "none", border: "none", cursor: "pointer", color: "#bdc3c7" };
const scrollAreaStyle = { flex: 1, overflowY: "auto", padding: "20px", background: "#fdfdfe" };
const itemCardStyle = { background: "#fff", borderRadius: "15px", padding: "18px", marginBottom: "15px", border: "1px solid #f0f0f0", boxShadow: "0 4px 12px rgba(0,0,0,0.03)" };
const itemHeader = { display: "flex", justifyContent: "space-between", marginBottom: "12px" };
const itemName = { margin: 0, fontSize: "1rem", fontWeight: 600, color: "#34495e" };
const itemMeta = { margin: 0, fontSize: "0.75rem", color: "#95a5a6", textTransform: "capitalize" };
const deleteBtn = { border: "none", background: "#fff5f5", color: "#e74c3c", padding: "8px", borderRadius: "8px", cursor: "pointer" };
const itemFooter = { display: "flex", justifyContent: "space-between", alignItems: "center" };
const qtyControl = { display: "flex", alignItems: "center", background: "#f8f9fa", borderRadius: "10px", padding: "5px" };
const qtyBtn = { width: "30px", height: "30px", border: "none", background: "#fff", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#3498db", boxShadow: "0 2px 5px rgba(0,0,0,0.05)" };
const qtyValue = { padding: "0 15px", fontWeight: 700, color: "#2c3e50" };
const priceArea = { textAlign: "right", display: "flex", flexDirection: "column" };
const oldPrice = { fontSize: "0.75rem", color: "#bdc3c7", textDecoration: "line-through" };
const newPrice = { fontSize: "1.1rem", fontWeight: 700, color: "#27ae60" };
const footerStyle = { padding: "25px", background: "#fff", borderTop: "1px solid #f1f1f1" };
const summaryRow = { display: "flex", justifyContent: "space-between", marginBottom: "8px", fontSize: "0.9rem", color: "#7f8c8d" };
const totalRow = { display: "flex", justifyContent: "space-between", margin: "15px 0", fontSize: "1.3rem", fontWeight: 700, color: "#2c3e50" };
const checkoutBtn = { width: "100%", padding: "18px", background: "#3498db", color: "#fff", border: "none", borderRadius: "12px", fontWeight: 700, fontSize: "1.1rem", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 10px 20px rgba(52, 152, 219, 0.2)" };
const emptyCartContainerStyle = { textAlign: "center", padding: "50px 20px" };
const emptyCartIconWrapper = { width: "100px", height: "100px", background: "#f8f9fa", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" };
const emptyHeading = { fontSize: "1.3rem", fontWeight: 700, color: "#2c3e50", margin: "0 0 10px" };
const emptyText = { fontSize: "0.9rem", color: "#7f8c8d", lineHeight: 1.5, marginBottom: "30px" };
const primaryBtn = { padding: "12px 30px", background: "#3498db", color: "#fff", border: "none", borderRadius: "10px", fontWeight: 600, cursor: "pointer" };

export default CartSidebar;